import { useCallback, useEffect, useRef, useState } from 'react'
import { useGameStore, Move } from '../stores/gameStore'
import { selectNeuralPolicyMove, selectNeuralMctsMove, warmupNeuralPolicy } from '../ai/neuralPolicy'
import { selectSimpleAIMove } from '../ai/simpleAi'
import { createNewGame, applyMove } from '../game/engine'
import { getMoveDurationMs } from '../utils/animation'

// Difficulty ladder: easy = centered greedy, medium = centered 2-ply minimax
// (heuristics); hard = neural policy-greedy, advanced = neural policy+value MCTS.
// The whole game runs client-side — createNewGame/applyMove replace the old
// backend, and the AI move selection was already in the browser.

export function useGame() {
  const store = useGameStore()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const browserAIInFlight = useRef(false)

  const startNewGame = useCallback(() => {
    setError(null)
    browserAIInFlight.current = false
    // Preload the neural model in the background so the first Hard/Advanced move
    // isn't stalled by the 6 MB download + session init.
    if (store.aiDifficulty === 'hard' || store.aiDifficulty === 'advanced') warmupNeuralPolicy()
    const gameState = createNewGame()
    store.setGameState({
      ...gameState,
      selectedPiece: null,
      validMoves: [],
      animatingPiece: null,
      isAIThinking: false,
    })
  }, [store])

  const makeMove = useCallback(
    async (move: Move) => {
      const s = useGameStore.getState()
      if (!s.gameId) return

      setIsLoading(true)
      setError(null)
      store.setGameState({
        isAIThinking: true,
        selectedPiece: null,
        validMoves: [],
      })

      try {
        const gameState = applyMove(
          {
            gameId: s.gameId,
            pieces: s.pieces,
            currentPlayer: s.currentPlayer,
            winner: s.winner,
            legalMoves: s.legalMoves,
            moveHistory: s.moveHistory,
          },
          move,
        )
        const animationMs = getMoveDurationMs(move.path)

        store.setAnimating({
          from: move.start,
          to: move.end,
          path: move.path,
        })

        window.setTimeout(() => {
          store.setAnimating(null)
          store.setGameState({
            ...gameState,
            selectedPiece: null,
            validMoves: [],
            isAIThinking: false,
          })
        }, animationMs)

        if (gameState.winner !== null) {
          const result =
            gameState.winner === 0
              ? 'draw'
              : gameState.winner === store.playerColor
              ? 'win'
              : 'loss'

          const opponentName = store.aiDifficulty === 'advanced'
            ? `Browser MCTS (${store.aiDifficulty})`
            : `Minimax AI (${store.aiDifficulty})`

          store.recordGameResult(
            result,
            gameState.moveHistory,
            opponentName,
          )
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        store.setGameState({ isAIThinking: false, animatingPiece: null })
      } finally {
        setIsLoading(false)
      }
    },
    [store],
  )

  const runBrowserAIMove = useCallback(async () => {
    if (browserAIInFlight.current) return
    if (!store.gameId || store.winner !== null || !store.legalMoves.length) return
    if (store.isAIThinking || store.animatingPiece) return

    const aiPlayer = store.playerColor === 1 ? 2 : 1
    if (store.currentPlayer !== aiPlayer) return

    browserAIInFlight.current = true
    store.setGameState({ isAIThinking: true })

    try {
      let move: Move | null = null
      const neuralArgs = {
        pieces: store.pieces,
        currentPlayer: store.currentPlayer,
        legalMoves: store.legalMoves,
      }

      // Route to appropriate AI based on difficulty
      if (store.aiDifficulty === 'advanced') {
        move = (await selectNeuralMctsMove(neuralArgs)).move
      } else if (store.aiDifficulty === 'hard') {
        move = (await selectNeuralPolicyMove(neuralArgs)).move
      } else {
        move = (await selectSimpleAIMove({ ...neuralArgs, difficulty: store.aiDifficulty })).move
      }

      if (move) {
        await makeMove(move)
      } else {
        store.setGameState({ isAIThinking: false })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      store.setGameState({ isAIThinking: false, animatingPiece: null })
    } finally {
      browserAIInFlight.current = false
    }
  }, [
    makeMove,
    store.currentPlayer,
    store.gameId,
    store.animatingPiece,
    store.aiDifficulty,
    store.isAIThinking,
    store.legalMoves,
    store.moveHistory.length,
    store.pieces,
    store.playerColor,
    store.winner,
  ])

  useEffect(() => {
    void runBrowserAIMove()
  }, [runBrowserAIMove])

  return {
    ...store,
    startNewGame,
    makeMove,
    error,
    isLoading,
  }
}
